import { Card, CbhIcon, Pill, Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { FullWidthCountdownClock } from "@src/appV2/redesign/components/FullWidthCountdownClock";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { format, utcToZonedTime } from "date-fns-tz";
import { generatePath, useHistory } from "react-router-dom";

import { Button } from "../components/Button";

export interface JobsMarketLaunchCountdownPageProps {
  jobFairHcfId: string;
  jobFairTimestamp: string;
  jobFairMsa: string;
  marketLaunchTimestamp: string;
  hasPendingJobFairInvite: boolean;
  hasJobFairShift: boolean;
}

export function JobsMarketLaunchCountdownPage(props: JobsMarketLaunchCountdownPageProps) {
  const worker = useDefinedWorker();
  const history = useHistory();

  const {
    hasPendingJobFairInvite,
    jobFairHcfId,
    jobFairTimestamp,
    jobFairMsa,
    marketLaunchTimestamp,
    hasJobFairShift,
  } = props;

  const marketLaunchDate = parseISO(marketLaunchTimestamp);
  const jobFairDate = parseISO(jobFairTimestamp);

  const userTimezone = worker.tmz ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formattedLocalJobFairDate = format(
    utcToZonedTime(jobFairDate, userTimezone),
    "MMMM do h:mm a"
  );

  const eventsData = {
    workerId: worker.userId,
    workplaceId: jobFairHcfId,
    jobFairMsa,
    marketLaunchDate,
    jobFairDate,
    localJobFairDate: formattedLocalJobFairDate,
    hasPendingJobFairInvite,
  };

  useLogEffect(APP_V2_APP_EVENTS.JOB_FAIR_COUNTDOWN_PAGE_VIEWED, eventsData);

  function handleAcceptInvite() {
    logEvent(APP_V2_APP_EVENTS.JOB_FAIR_COUNTDOWN_PAGE_INVITE_CLICKED, eventsData);

    history.push(generatePath(`${RootPaths.APP_V2_HOME}/myShifts/shiftInvites`));
  }

  return (
    <Stack
      direction="column"
      justifyContent="flex-start"
      spacing={6}
      padding={8}
      paddingBottom={12}
      sx={{ overflowY: "auto" }}
    >
      <Text semibold variant="h4" textAlign="center">
        Coming soon: Clipboard Jobs
      </Text>

      <FullWidthCountdownClock endTime={marketLaunchDate} />

      <Stack direction="column" gap={5}>
        <Text bold>Starting {format(marketLaunchDate, "MMMM do")}</Text>

        <Stack direction="row" alignItems="center" spacing={5}>
          <CbhIcon type="clock" size="large" />

          <Stack direction="column" spacing={1}>
            <Text bold variant="body1">
              Find Staff Jobs
            </Text>

            <Text variant="body2" color="textSecondary">
              Explore Full-Time, Part-Time, and PRN jobs right here on Clipboard
            </Text>
          </Stack>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={5}>
          <CbhIcon type="calendar-with-cells" size="large" />
          <Stack direction="column" spacing={1}>
            <Text bold variant="body1">
              Instant Book Interviews
            </Text>
            <Text variant="body2" color="textSecondary">
              Don&apos;t wait for a call back — book your interview directly on Clipboard
            </Text>
          </Stack>
        </Stack>
      </Stack>

      <Card
        sx={(theme) => ({
          // eslint-disable-next-line cbh-rules/no-inline-gradient -- TODO: migrate to theme palette gradient
          background: "linear-gradient(312.93deg, #FAF8F7 34.17%, #E2F1FF 88.75%)",
          border: `1px solid ${theme.palette.info.dark}`,
          borderRadius: theme.shape.borderRadius,
          padding: theme.spacing(5),
          minHeight: "fit-content",
        })}
      >
        <Stack direction="column" spacing={2}>
          <Box>
            <Pill
              size="small"
              variant="filled"
              label="Get early access"
              backgroundColor={(theme) => theme.palette.info.light}
            />
          </Box>

          <Box>
            <Stack gap={1}>
              <Text bold variant="h6">
                Kick-off Event: Job Fair
              </Text>
              <Text bold variant="h6">
                {formattedLocalJobFairDate}
              </Text>
            </Stack>

            <Box component="ul" sx={{ gap: 2, flexDirection: "column", display: "flex" }}>
              <Box component="li">
                <Text variant="body2">
                  Complete 1:1 interviews in a quiet space — no waiting at a crowded booth
                </Text>
              </Box>
              <Box component="li">
                <Text variant="body2">
                  Meet 40+ employers from {jobFairMsa} and leave with an offer — many are hiring
                  same-day!
                </Text>
              </Box>
            </Box>

            {hasPendingJobFairInvite && (
              <Box>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    handleAcceptInvite();
                  }}
                >
                  Accept your job fair invite now!
                </Button>
              </Box>
            )}
            {hasJobFairShift && (
              <Text semibold align="center" variant="body2" sx={{ paddingTop: 2 }}>
                You&apos;re all set for the fair, see you there!
              </Text>
            )}
          </Box>
        </Stack>
      </Card>
    </Stack>
  );
}
